/**
 * Zod Schemas for Prebid Multi-Site Management System
 * 
 * These schemas validate our configuration structure while preserving
 * the correct bidder parameter architecture (nested video params are necessary
 * because many bidders require different IDs for video vs banner inventory).
 */

import { z } from 'zod';

// ============================================================================
// Base Schemas
// ============================================================================

const SizeSchema = z.array(z.number()).length(2);
const SizesSchema = z.array(SizeSchema);

// ============================================================================
// Media Type Schemas
// ============================================================================

const BannerSizesSchema = z.object({
  desktop: SizesSchema,
  mobile: SizesSchema
});

const VideoSettingsSchema = z.object({
  context: z.enum(['instream', 'outstream']),
  playerSize: SizeSchema,
  mimes: z.array(z.string()).default(['video/mp4']),
  protocols: z.array(z.number()).default([2, 3, 5, 6]),
  playbackmethod: z.array(z.number()).default([6]),
  minduration: z.number().min(1).default(5),
  maxduration: z.number().max(120).default(30),
  placement: z.number().optional(),
  plcmt: z.number().optional(),
  startdelay: z.number().optional(),
  linearity: z.number().optional(),
  skip: z.number().optional(),
  skipmin: z.number().optional(),
  skipafter: z.number().optional(),
  api: z.array(z.number()).optional(),
  renderer: z.object({
    url: z.string().url().default('https://cdn.jsdelivr.net/npm/in-renderer-js@1/dist/in-renderer.umd.min.js'),
    autoPlay: z.boolean().default(true),
    mute: z.boolean().default(true),
    adText: z.string().default('Advertisement')
  }).optional()
});

// ============================================================================
// Bidder Parameter Schemas (PRESERVE NESTED STRUCTURE)
// ============================================================================

// Criteo bidder params
const CriteoParamsSchema = z.object({
  networkId: z.number()
});

// SmartAdServer bidder params  
const SmartAdServerParamsSchema = z.object({
  formatId: z.string(),
  siteId: z.number().optional(),
  pageId: z.number().optional()
});

// Sovrn bidder params (CORRECT: nested video with different tagid)
const SovrnParamsSchema = z.object({
  tagid: z.string(),
  video: z.object({
    tagid: z.string()  // Different video tagid - REQUIRED
  }).optional()
});

// Teads bidder params
const TeadsParamsSchema = z.object({
  placementId: z.number(),
  pageId: z.number().optional()
});

// TripleLift bidder params
const TripleLiftParamsSchema = z.object({
  inventoryCode: z.string(),
  floor: z.number().optional()
});

// ADF bidder params (CORRECT: nested video with different mid)
const ADFParamsSchema = z.object({
  mid: z.union([z.string(), z.number()]),
  video: z.object({
    mid: z.union([z.string(), z.number()])  // Different video mid - REQUIRED
  }).optional()
});

// Generic bidder params schema (preserves nested structure)
const BidderParamsSchema = z.record(z.any()).and(
  z.object({
    video: z.record(z.any()).optional()  // Nested video params preserved
  })
);

// ============================================================================
// Context Schema
// ============================================================================

const ContextSchema = z.object({
  enabled: z.boolean(),
  sizes: BannerSizesSchema,
  gamPath: z.string(),
  
  // PRESERVE: Bidder params with nested video structure (it's correct!)
  bidderParams: z.record(BidderParamsSchema).optional()
  
  // REMOVED: mediaType (now at placement level)
  // REMOVED: videoSettings (now at placement level)
});

// ============================================================================
// Settings Schema (Cleaned up)
// ============================================================================

const LazyLoadMarginSchema = z.object({
  desktop: z.string().default('800px'),
  mobile: z.string().default('600px')
});

const PlacementSettingsSchema = z.object({
  lazyLoad: z.boolean().default(false),
  prefetch: z.boolean().default(true),
  refresh: z.boolean().default(false),
  fluid: z.boolean().default(true),
  lazyLoadMargin: LazyLoadMarginSchema.default({
    desktop: '800px',
    mobile: '600px'
  }),
  refreshInterval: z.number().optional(),
  deviceTargeting: z.array(z.string()).optional()
  
  // REMOVED: mediaType (now at placement level)
  // REMOVED: videoSettings (now at placement level)  
});

// ============================================================================
// Placement Schema (FIXED ARCHITECTURE)
// ============================================================================

const PlacementSchema = z.object({
  selector: z.string(),
  
  // FIX 1: Single mediaType at placement level (not duplicated)
  mediaType: z.enum(['banner', 'video', 'both']).default('banner'),
  
  // FIX 2: Video settings at placement level (shared across contexts)
  videoSettings: VideoSettingsSchema.optional(),
  
  // Context overrides (no mediaType or videoSettings here)
  contexts: z.record(ContextSchema),
  
  // Clean settings (no mediaType or videoSettings here)
  settings: PlacementSettingsSchema
});

// ============================================================================
// Site Configuration Schemas
// ============================================================================

const CustomPriceGranularitySchema = z.object({
  buckets: z.array(z.object({
    precision: z.number(),
    min: z.number(),
    max: z.number(),
    increment: z.number()
  }))
});

const AnalyticsConfigSchema = z.object({
  cxense: z.object({
    enabled: z.boolean().default(false),
    siteId: z.string().nullable().optional(),
    contextualQueryId: z.string().optional(),
    userSegmentQueryId: z.string().optional(),
    customParameters: z.record(z.any()).default({})
  }).optional()
});

// Timing configuration schema - replaces all hardcoded values
const TimingConfigSchema = z.object({
  // Device detection
  deviceBreakpoint: z.number().default(768),
  
  // GPT settings  
  gptTimeout: z.number().default(5000),
  
  // Refresh settings
  refreshCooldown: z.number().default(1000),
  maxRefreshes: z.number().default(10),
  refreshMinInterval: z.number().default(15),
  refreshMaxInterval: z.number().default(300),
  preAuctionRatio: z.number().default(0.75),
  scrollActivityTimeout: z.number().default(2000),
  
  // Lazy loading
  lazyLoadMargin: z.object({
    desktop: z.string().default('1000px'),
    mobile: z.string().default('1000px')
  }).default({ desktop: '1000px', mobile: '1000px' }),
  lazyBatchSize: z.number().default(3),
  lazyProcessDelay: z.number().default(50),
  lazyImmediateThreshold: z.number().default(100),
  
  // Analytics
  analyticsBatchSize: z.number().default(25),
  analyticsMaxWaitTime: z.number().default(2000),
  analyticsMinBatchSize: z.number().default(5),
  analyticsPriorityEvents: z.array(z.string()).default(['auction_end', 'bid_won']),
  analyticsEndpoint: z.string().url().default('https://api.dimitriu.xyz/api/analytics/beacon'),
  
  // Privacy/Consent
  consentTimeout: z.number().default(10000),
  
  // Script loading
  prebidMaxChecks: z.number().default(5),
  prebidCheckInterval: z.number().default(200)
}).optional();

const SiteConfigSchema = z.object({
  siteId: z.string(),
  siteName: z.string(),
  domain: z.string(),
  currency: z.string().default('EUR'),
  prebidTimeout: z.number().default(2000),
  debugMode: z.boolean().default(false),
  contextDefinitions: z.record(z.string()).default({
    "1": "article",
    "2": "homepage", 
    "3": "category"
  }),
  priceGranularity: z.enum(['low', 'medium', 'high', 'auto', 'dense', 'custom']).default('medium'),
  customPriceGranularity: CustomPriceGranularitySchema.optional(),
  analytics: AnalyticsConfigSchema.optional(),
  timing: TimingConfigSchema
});

// ============================================================================
// Bidder Configuration Schemas
// ============================================================================

const BidderConfigSchema = z.object({
  enabled: z.boolean(),
  bidAdjustment: z.number().optional()
}).passthrough(); // Allow additional bidder-specific config

const BiddersSchema = z.object({
  criteo: BidderConfigSchema.extend({
    networkId: z.number()
  }).optional(),
  teads: BidderConfigSchema.optional(),
  triplelift: BidderConfigSchema.optional(), 
  smartadserver: BidderConfigSchema.extend({
    siteId: z.number(),
    pageId: z.number()
  }).optional(),
  sovrn: BidderConfigSchema.optional(),
  adf: BidderConfigSchema.extend({
    mid: z.union([z.string(), z.number()])
  }).optional(),
  appnexus: BidderConfigSchema.optional(),
  projectagora: BidderConfigSchema.extend({
    sid: z.string()
  }).optional(),
  sspBC: BidderConfigSchema.optional()
}).passthrough(); // Allow additional bidders

// ============================================================================
// User ID Module Schema
// ============================================================================

const UserIdModuleSchema = z.object({
  expiry: z.number().default(30),
  partner: z.number().optional(),
  pubId: z.number().optional()
});

const UserIdModulesSchema = z.record(UserIdModuleSchema);

// ============================================================================
// Main Configuration Schema
// ============================================================================

export const ConfigurationSchema = z.object({
  site: SiteConfigSchema,
  bidders: BiddersSchema,
  userIdModules: UserIdModulesSchema.optional(),
  placements: z.record(PlacementSchema)
});

// ============================================================================
// Validation Utilities
// ============================================================================

/**
 * Validate a complete site configuration
 */
export function validateConfiguration(config) {
  return ConfigurationSchema.parse(config);
}

/**
 * Safely validate configuration with detailed error handling
 */
export function validateConfigurationSafe(config) {
  try {
    const validated = validateConfiguration(config);
    return { success: true, data: validated, errors: null };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        data: null,
        errors: error.errors.map(err => ({
          path: err.path.join('.'),
          message: err.message,
          code: err.code
        }))
      };
    }
    return {
      success: false,
      data: null,
      errors: [{ path: 'unknown', message: error.message, code: 'UNKNOWN_ERROR' }]
    };
  }
}

/**
 * Validate just a placement
 */
export function validatePlacement(placement) {
  return PlacementSchema.parse(placement);
}

/**
 * Safely validate a placement
 */
export function validatePlacementSafe(placement) {
  try {
    const validated = validatePlacement(placement);
    return { success: true, data: validated, errors: null };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        data: null,
        errors: error.errors.map(err => ({
          path: err.path.join('.'),
          message: err.message,
          code: err.code
        }))
      };
    }
    return {
      success: false,
      data: null,
      errors: [{ path: 'unknown', message: error.message, code: 'UNKNOWN_ERROR' }]
    };
  }
}

// ============================================================================
// Export Individual Schemas
// ============================================================================

export {
  PlacementSchema,
  SiteConfigSchema,
  BiddersSchema,
  VideoSettingsSchema,
  BidderParamsSchema,
  ContextSchema,
  PlacementSettingsSchema
};